/**
 * WysiwygOverlay - Canvas overlay for Wix-style WYSIWYG interactions
 * 
 * Renders on a top-level absolutely-positioned layer aligned to canvas.
 * Uses getBoundingClientRect() from DOM for accurate positioning.
 * 
 * Features:
 * - Blue selection bounding box (2px #3B82F6)
 * - Hover outline (1px blue 50% opacity)
 * - Resize handles (white fill, blue border, 8px)
 * - Orange label pill (top-left of bounding box)
 * - Drag move (constrained to parent section)
 * - Inline text editing (contentEditable)
 */

import React, { useEffect, useRef, useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { useEditorStore, EDITOR_MODES, INTERACTIVE_TYPES } from './useEditorStore';

// Colors
const SELECTION_COLOR = '#3B82F6';
const HOVER_COLOR = 'rgba(59, 130, 246, 0.5)';
const LABEL_BG = '#F97316'; // Trap Comix orange

// Min sizes per element type
const MIN_SIZES = {
  text: { w: 40, h: 20 },
  image: { w: 40, h: 40 },
  button: { w: 60, h: 28 },
};

// Resize handle positions
const HANDLE_POSITIONS = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];

const getHandleStyle = (handle, rect) => {
  const size = 8;
  const half = size / 2;
  const base = {
    position: 'absolute',
    width: size,
    height: size,
    background: '#fff',
    border: `2px solid ${SELECTION_COLOR}`,
    borderRadius: 1,
    cursor: `${handle}-resize`,
    zIndex: 10,
  };
  
  const pos = {
    nw: { top: -half, left: -half },
    n:  { top: -half, left: '50%', marginLeft: -half },
    ne: { top: -half, right: -half },
    e:  { top: '50%', right: -half, marginTop: -half },
    se: { bottom: -half, right: -half },
    s:  { bottom: -half, left: '50%', marginLeft: -half },
    sw: { bottom: -half, left: -half },
    w:  { top: '50%', left: -half, marginTop: -half },
  };
  
  return { ...base, ...pos[handle] };
};

/**
 * SelectionBox - The blue bounding box around a selected element
 */
const SelectionBox = ({ rect, elementType, elementLabel, onHandleMouseDown }) => {
  if (!rect) return null;
  
  return createPortal(
    <div
      className="fixed pointer-events-none"
      style={{
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
        zIndex: 9990,
      }}
      data-testid="wysiwyg-selection-box"
    >
      {/* Blue bounding box */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          border: `2px solid ${SELECTION_COLOR}`,
          borderRadius: 2,
          pointerEvents: 'none',
        }}
      />
      
      {/* Orange label pill */}
      <div
        style={{
          position: 'absolute',
          top: -24,
          left: 0,
          background: LABEL_BG,
          color: '#fff',
          fontSize: 10,
          fontWeight: 600,
          padding: '2px 8px',
          borderRadius: 3,
          whiteSpace: 'nowrap',
          pointerEvents: 'none',
          lineHeight: '16px',
        }}
        data-testid="wysiwyg-element-label"
      >
        {elementType}{elementLabel ? ` — ${elementLabel}` : ''}
      </div>
      
      {/* Resize handles */}
      {HANDLE_POSITIONS.map(handle => (
        <div
          key={handle}
          style={{
            ...getHandleStyle(handle),
            pointerEvents: 'auto',
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onHandleMouseDown(handle, e);
          }}
          data-testid={`resize-handle-${handle}`}
        />
      ))}
    </div>,
    document.body
  );
};

/**
 * HoverOutline - Subtle blue outline on hovered elements
 */
const HoverOutline = ({ rect }) => {
  if (!rect) return null;
  
  return createPortal(
    <div
      className="fixed pointer-events-none"
      style={{
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
        border: `1px solid ${HOVER_COLOR}`,
        borderRadius: 2,
        zIndex: 9985,
      }}
      data-testid="wysiwyg-hover-outline"
    />,
    document.body
  );
};

/**
 * InlineTextEditor - contentEditable overlay for text elements
 */
const InlineTextEditor = ({ rect, initialText, onCommit, onCancel }) => {
  const editorRef = useRef(null);
  
  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.innerText = initialText || '';
      editorRef.current.focus();
      // Select all text
      const range = document.createRange();
      range.selectNodeContents(editorRef.current);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    }
  }, []);
  
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
    }
    // Prevent propagation so editor shortcuts don't fire
    e.stopPropagation();
  };
  
  const handleBlur = () => {
    const newText = editorRef.current?.innerText?.trim() || '';
    onCommit(newText);
  };
  
  if (!rect) return null;
  
  return createPortal(
    <div
      ref={editorRef}
      contentEditable
      suppressContentEditableWarning
      className="fixed outline-none"
      style={{
        top: rect.top,
        left: rect.left,
        width: rect.width,
        minHeight: rect.height,
        zIndex: 9995,
        background: 'rgba(0,0,0,0.8)',
        border: `2px solid ${SELECTION_COLOR}`,
        borderRadius: 2,
        color: '#fff',
        padding: '4px 6px',
        fontSize: 'inherit',
        fontFamily: 'inherit',
        lineHeight: 'inherit',
        cursor: 'text',
        boxSizing: 'border-box',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
      }}
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
      data-testid="wysiwyg-inline-text-editor"
    />,
    document.body
  );
};

/**
 * WysiwygOverlay - Main overlay component
 * 
 * Props:
 * - canvasRef: Ref to the canvas container
 * - onCommitChanges: Callback for persisting changes (drag, resize, text)
 * - onSelectElement: Callback to sync selection with parent (elementId, blockId)
 * - selectedElementId: Currently selected element ID from parent
 * - selectedBlockId: Currently selected block ID from parent
 */
const WysiwygOverlay = ({ 
  canvasRef, 
  onCommitChanges,
  onSelectElement,
  selectedElementId: externalSelectedId,
  selectedBlockId: externalBlockId,
}) => {
  const store = useEditorStore();
  const [selectedRect, setSelectedRect] = useState(null);
  const [hoveredRect, setHoveredRect] = useState(null);
  const [selectedElementType, setSelectedElementType] = useState('');
  const [selectedElementLabel, setSelectedElementLabel] = useState('');
  const [inlineEditText, setInlineEditText] = useState('');
  
  const dragStateRef = useRef(null);
  const resizeStateRef = useRef(null);
  const rafRef = useRef(null);
  
  // Find element in DOM by data attribute
  const findElement = useCallback((elementId) => {
    if (!canvasRef?.current || !elementId) return null;
    return canvasRef.current.querySelector(`[data-tc-element-id="${elementId}"]`);
  }, [canvasRef]);
  
  // Get parent section bounds for constraining
  const getParentBounds = useCallback((elementId) => {
    const el = findElement(elementId);
    if (!el) return null;
    const section = el.closest('[data-block-id]');
    if (!section) return null;
    return section.getBoundingClientRect();
  }, [findElement]);
  
  // Update selected element rect from DOM
  const updateSelectedRect = useCallback(() => {
    // Use external selection if provided, otherwise use store
    const activeSelectedId = externalSelectedId || store.selectedId;
    const el = findElement(activeSelectedId);
    if (el) {
      const r = el.getBoundingClientRect();
      setSelectedRect({ top: r.top, left: r.left, width: r.width, height: r.height, right: r.right, bottom: r.bottom });
      setSelectedElementType(el.dataset.tcElementType || 'element');
      setSelectedElementLabel(el.dataset.tcElementLabel || '');
    } else {
      setSelectedRect(null);
    }
  }, [externalSelectedId, store.selectedId, findElement]);
  
  // Update hovered element rect from DOM
  const updateHoveredRect = useCallback(() => {
    const activeSelectedId = externalSelectedId || store.selectedId;
    if (store.hoveredId && store.hoveredId !== activeSelectedId) {
      const el = findElement(store.hoveredId);
      if (el) {
        const r = el.getBoundingClientRect();
        setHoveredRect({ top: r.top, left: r.left, width: r.width, height: r.height });
        return;
      }
    }
    setHoveredRect(null);
  }, [store.hoveredId, externalSelectedId, store.selectedId, findElement]);
  
  // Continuously update rects on scroll/resize
  useEffect(() => {
    const update = () => {
      updateSelectedRect();
      updateHoveredRect();
      rafRef.current = requestAnimationFrame(update);
    };
    rafRef.current = requestAnimationFrame(update);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [updateSelectedRect, updateHoveredRect]);
  
  // =========================================================================
  // DRAG HANDLING (Constraint-Aware, Following Best Practices)
  // Step 1: Visual drag state only (no API calls during drag)
  // Step 2: Constrain within parent bounds
  // Step 3: Axis lock with Shift key
  // Step 4: Cursor + UX polish
  // =========================================================================
  const handleDragStart = useCallback((e) => {
    if (store.mode === EDITOR_MODES.TEXT_EDIT) return;
    
    const activeSelectedId = externalSelectedId || store.selectedId;
    if (!activeSelectedId) return;
    
    const el = findElement(activeSelectedId);
    if (!el) return;
    if (!INTERACTIVE_TYPES.includes(el.dataset.tcElementType)) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    // Get element and parent bounds
    const childRect = el.getBoundingClientRect();
    const parentEl = el.closest('[data-block-id]');
    const parentRect = parentEl ? parentEl.getBoundingClientRect() : null;
    
    // Store drag origin state
    dragStateRef.current = {
      startMouseX: e.clientX,
      startMouseY: e.clientY,
      childRect,
      parentRect,
      lockedAxis: null, // Will be set on first significant movement with Shift
    };
    
    store.startDrag({ x: e.clientX, y: e.clientY });
    
    // UX: Set cursor to grabbing, disable text selection
    document.body.style.cursor = 'grabbing';
    document.body.style.userSelect = 'none';
    
    const handleMouseMove = (moveEvent) => {
      if (!dragStateRef.current) return;
      const ds = dragStateRef.current;
      
      let dx = moveEvent.clientX - ds.startMouseX;
      let dy = moveEvent.clientY - ds.startMouseY;
      
      // Step 3: Axis Lock - Hold Shift to lock to dominant axis
      if (moveEvent.shiftKey) {
        // Lock to axis on first significant movement
        if (!ds.lockedAxis && (Math.abs(dx) > 5 || Math.abs(dy) > 5)) {
          ds.lockedAxis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        }
        if (ds.lockedAxis === 'x') {
          dy = 0;
        } else if (ds.lockedAxis === 'y') {
          dx = 0;
        }
      } else {
        // Reset lock if Shift released
        ds.lockedAxis = null;
      }
      
      // Step 2: Constrain within parent bounds
      if (ds.parentRect) {
        // Calculate where element would be after drag
        const newLeft = ds.childRect.left + dx - ds.parentRect.left;
        const newTop = ds.childRect.top + dy - ds.parentRect.top;
        
        // Clamp to stay within parent
        const maxX = ds.parentRect.width - ds.childRect.width;
        const maxY = ds.parentRect.height - ds.childRect.height;
        
        const clampedX = Math.max(0, Math.min(newLeft, maxX));
        const clampedY = Math.max(0, Math.min(newTop, maxY));
        
        // Recalculate dx/dy based on clamped position
        dx = clampedX - (ds.childRect.left - ds.parentRect.left);
        dy = clampedY - (ds.childRect.top - ds.parentRect.top);
      }
      
      // Step 1: Visual feedback only - use CSS transform (no DOM mutation)
      el.style.transform = `translate(${dx}px, ${dy}px)`;
      el.style.zIndex = '100';
      el.style.opacity = '0.9';
      el.style.pointerEvents = 'none';
      
      // Update store state (local only, no API calls)
      store.updateDrag({ dx, dy });
    };
    
    const handleMouseUp = (e) => {
      if (!dragStateRef.current) return;
      
      // Prevent click event from bubbling and selecting section
      e.stopPropagation();
      e.preventDefault();
      
      // Get final offset from store before resetting
      const finalDx = store.dragOffset?.dx || 0;
      const finalDy = store.dragOffset?.dy || 0;
      
      // Reset inline styles
      el.style.transform = '';
      el.style.zIndex = '';
      el.style.opacity = '';
      el.style.pointerEvents = '';
      
      // Reset cursor and text selection
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      
      // Only commit if moved more than 2px (debounced persistence)
      if (Math.abs(finalDx) > 2 || Math.abs(finalDy) > 2) {
        // Commit to store via props._layout (not direct DOM mutation)
        onCommitChanges?.('drag', activeSelectedId, store.selectedBlockId || externalBlockId, {
          dx: Math.round(finalDx),
          dy: Math.round(finalDy),
        });
      }
      
      store.endDrag();
      dragStateRef.current = null;
      
      // Re-assert element selection after drag completes
      // This prevents selection from moving to section level
      setTimeout(() => {
        if (onSelectElement && activeSelectedId) {
          onSelectElement(activeSelectedId, store.selectedBlockId || externalBlockId);
        }
      }, 10);
      
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  }, [store, findElement, onCommitChanges, externalSelectedId, externalBlockId]);
  
  // =========================================================================
  // RESIZE HANDLING (MVP: Corner handles only, respect min sizes)
  // =========================================================================
  const handleResizeStart = useCallback((handle, e) => {
    const activeSelectedId = externalSelectedId || store.selectedId;
    if (!activeSelectedId) return;
    
    const el = findElement(activeSelectedId);
    if (!el) return;
    if (!INTERACTIVE_TYPES.includes(el.dataset.tcElementType)) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    const rect = el.getBoundingClientRect();
    const elType = el.dataset.tcElementType || 'text';
    const minSize = MIN_SIZES[elType] || MIN_SIZES.text;
    
    // Get parent bounds for constraint
    const parentEl = el.closest('[data-block-id]');
    const parentRect = parentEl ? parentEl.getBoundingClientRect() : null;
    
    resizeStateRef.current = {
      handle,
      startX: e.clientX,
      startY: e.clientY,
      startW: rect.width,
      startH: rect.height,
      startLeft: rect.left,
      startTop: rect.top,
      minW: minSize.w,
      minH: minSize.h,
      parentRect,
    };
    
    store.startResize(handle, { x: rect.left, y: rect.top, w: rect.width, h: rect.height });
    
    // UX: Set cursor to resize, disable text selection
    document.body.style.cursor = `${handle}-resize`;
    document.body.style.userSelect = 'none';
    
    const handleMouseMove = (moveEvent) => {
      if (!resizeStateRef.current) return;
      const s = resizeStateRef.current;
      
      const dx = moveEvent.clientX - s.startX;
      const dy = moveEvent.clientY - s.startY;
      
      let newW = s.startW;
      let newH = s.startH;
      
      // Calculate new dimensions based on handle (corner handles prioritized)
      if (handle.includes('e')) newW = s.startW + dx;
      if (handle.includes('w')) newW = s.startW - dx;
      if (handle.includes('s')) newH = s.startH + dy;
      if (handle.includes('n')) newH = s.startH - dy;
      
      // Enforce minimum sizes (no negative or zero dimensions)
      newW = Math.max(s.minW, newW);
      newH = Math.max(s.minH, newH);
      
      // Constrain to parent bounds if available
      if (s.parentRect) {
        const maxW = s.parentRect.width - (s.startLeft - s.parentRect.left);
        const maxH = s.parentRect.height - (s.startTop - s.parentRect.top);
        newW = Math.min(newW, maxW);
        newH = Math.min(newH, maxH);
      }
      
      // Apply live via inline styles
      el.style.width = `${newW}px`;
      el.style.height = `${newH}px`;
      
      store.updateResize({ w: newW, h: newH });
    };
    
    const handleMouseUp = () => {
      if (!resizeStateRef.current) return;
      
      const el = findElement(activeSelectedId);
      const finalW = el ? el.getBoundingClientRect().width : resizeStateRef.current.startW;
      const finalH = el ? el.getBoundingClientRect().height : resizeStateRef.current.startH;
      
      // Reset inline styles
      if (el) {
        el.style.width = '';
        el.style.height = '';
      }
      
      // Reset cursor and text selection
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      
      // Commit via props._layout (not direct DOM mutation)
      onCommitChanges?.('resize', activeSelectedId, store.selectedBlockId || externalBlockId, {
        w: Math.round(finalW),
        h: Math.round(finalH),
      });
      
      store.endResize();
      resizeStateRef.current = null;
      
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  }, [store, findElement, onCommitChanges, externalSelectedId, externalBlockId]);
  
  // =========================================================================
  // INLINE TEXT EDITING
  // =========================================================================
  const handleStartTextEdit = useCallback(() => {
    if (store.mode === EDITOR_MODES.TEXT_EDIT) return;
    
    const el = findElement(store.selectedId);
    if (!el || el.dataset.tcElementType !== 'text') return;
    
    const currentText = el.innerText || el.textContent || '';
    setInlineEditText(currentText);
    store.startTextEdit(currentText);
  }, [store, findElement]);
  
  const handleTextCommit = useCallback((newText) => {
    if (store.editingText !== null && newText !== store.editingText) {
      onCommitChanges?.('text', store.selectedId, store.selectedBlockId, {
        text: newText,
      });
    }
    store.endTextEdit();
    setInlineEditText('');
  }, [store, onCommitChanges]);
  
  const handleTextCancel = useCallback(() => {
    store.endTextEdit();
    setInlineEditText('');
  }, [store]);
  
  // =========================================================================
  // CLICK HANDLING (delegated from canvas)
  // =========================================================================
  useEffect(() => {
    if (!canvasRef?.current) return;
    
    const canvas = canvasRef.current;
    
    const handleClick = (e) => {
      // Find closest element with data-tc-element-id
      const targetEl = e.target.closest('[data-tc-element-id]');
      
      if (targetEl) {
        const elementId = targetEl.dataset.tcElementId;
        const blockId = targetEl.closest('[data-block-id]')?.dataset.blockId;
        const elType = targetEl.dataset.tcElementType;
        
        console.info('[WysiwygOverlay] Click on element:', { elementId, blockId, elType, tagName: targetEl.tagName });
        
        if (INTERACTIVE_TYPES.includes(elType)) {
          // Stop propagation to prevent section wrapper from selecting the section
          e.stopPropagation();
          
          // If same element already selected, skip
          const currentSelectedId = externalSelectedId || store.selectedId;
          if (currentSelectedId === elementId) {
            return;
          }
          
          // Update both: parent state (if callback provided) and internal store
          if (onSelectElement) {
            onSelectElement(elementId, blockId);
          }
          store.selectElement(elementId, blockId);
          return;
        }
      }
      
      // Clicked on canvas but not on an annotated element → deselect
      const currentSelectedId = externalSelectedId || store.selectedId;
      if (currentSelectedId) {
        if (onSelectElement) {
          onSelectElement(null, null);
        }
        store.deselect();
      }
    };
    
    const handleDblClick = (e) => {
      const targetEl = e.target.closest('[data-tc-element-id]');
      if (!targetEl) return;
      
      const elType = targetEl.dataset.tcElementType;
      if (elType === 'text') {
        e.stopPropagation();
        e.preventDefault();
        // Select first if not already
        const elementId = targetEl.dataset.tcElementId;
        const blockId = targetEl.closest('[data-block-id]')?.dataset.blockId;
        if (store.selectedId !== elementId) {
          store.selectElement(elementId, blockId);
        }
        // Then enter text edit mode
        setTimeout(() => handleStartTextEdit(), 50);
      }
    };
    
    const handleMouseOver = (e) => {
      if (store.mode === EDITOR_MODES.DRAG || store.mode === EDITOR_MODES.RESIZE) return;
      
      const targetEl = e.target.closest('[data-tc-element-id]');
      if (targetEl) {
        const elementId = targetEl.dataset.tcElementId;
        const blockId = targetEl.closest('[data-block-id]')?.dataset.blockId;
        if (INTERACTIVE_TYPES.includes(targetEl.dataset.tcElementType)) {
          store.hoverElement(elementId, blockId);
        }
      }
    };
    
    const handleMouseOut = (e) => {
      const targetEl = e.target.closest('[data-tc-element-id]');
      if (targetEl && targetEl.dataset.tcElementId === store.hoveredId) {
        store.clearHover();
      }
    };
    
    // Mouse down on selected element starts drag
    const handleMouseDown = (e) => {
      if (store.mode === EDITOR_MODES.TEXT_EDIT) return;
      
      const targetEl = e.target.closest('[data-tc-element-id]');
      if (!targetEl) return;
      
      const elementId = targetEl.dataset.tcElementId;
      if (elementId === store.selectedId && INTERACTIVE_TYPES.includes(targetEl.dataset.tcElementType)) {
        // Don't start drag on resize handles
        if (e.target.closest('[data-testid^="resize-handle"]')) return;
        e.stopPropagation();
        handleDragStart(e);
      }
    };
    
    // Use CAPTURE phase to intercept clicks before section wrappers
    canvas.addEventListener('click', handleClick, true);
    canvas.addEventListener('dblclick', handleDblClick, true);
    canvas.addEventListener('mouseover', handleMouseOver);
    canvas.addEventListener('mouseout', handleMouseOut);
    canvas.addEventListener('mousedown', handleMouseDown, true);
    
    return () => {
      canvas.removeEventListener('click', handleClick, true);
      canvas.removeEventListener('dblclick', handleDblClick, true);
      canvas.removeEventListener('mouseover', handleMouseOver);
      canvas.removeEventListener('mouseout', handleMouseOut);
      canvas.removeEventListener('mousedown', handleMouseDown, true);
    };
  }, [canvasRef, store, handleDragStart, handleStartTextEdit, onSelectElement, externalSelectedId]);
  
  // =========================================================================
  // KEYBOARD SHORTCUTS
  // =========================================================================
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Escape: deselect or cancel text edit
      if (e.key === 'Escape') {
        if (store.mode === EDITOR_MODES.TEXT_EDIT) {
          handleTextCancel();
        } else if (store.selectedId) {
          store.deselect();
        }
      }
      
      // Delete/Backspace: delete selected element (not during text edit)
      if ((e.key === 'Delete' || e.key === 'Backspace') && store.selectedId && store.mode !== EDITOR_MODES.TEXT_EDIT) {
        e.preventDefault();
        // Element deletion could be added here
      }
      
      // Ctrl+Z / Cmd+Z: undo
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        store.undo();
      }
      
      // Ctrl+Shift+Z / Cmd+Shift+Z: redo
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && e.shiftKey) {
        e.preventDefault();
        store.redo();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [store, handleTextCancel]);
  
  // =========================================================================
  // RENDER
  // =========================================================================
  const isTextEditing = store.mode === EDITOR_MODES.TEXT_EDIT && store.selectedId;
  
  return (
    <>
      {/* Hover outline */}
      {hoveredRect && !isTextEditing && (
        <HoverOutline rect={hoveredRect} />
      )}
      
      {/* Selection box with handles (hide during text edit) */}
      {selectedRect && !isTextEditing && (
        <SelectionBox
          rect={selectedRect}
          elementType={selectedElementType}
          elementLabel={selectedElementLabel}
          onHandleMouseDown={handleResizeStart}
        />
      )}
      
      {/* Inline text editor */}
      {isTextEditing && selectedRect && (
        <InlineTextEditor
          rect={selectedRect}
          initialText={inlineEditText}
          onCommit={handleTextCommit}
          onCancel={handleTextCancel}
        />
      )}
    </>
  );
};

export default WysiwygOverlay;
